import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Pencil1Icon, UpdateIcon } from '@radix-ui/react-icons';
import { EditorContent } from '@tiptap/react';
import { IconButton, useRichTextEditor } from '@producer/ui-kit';

import Sunset from '../../../components/icons/weather-icons/Sunset';
import Sunrise from '../../../components/icons/weather-icons/Sunrise';
import CloudAndWindy from '../../../components/icons/weather-icons/CloudAndWindy';
import { InfoCard } from './InfoCard';
import { WeatherModal } from './weather.modal';
import { useResponsive } from '../../../hooks/useResponsive';

import type { Step } from '../../../app/entities';
import { EditorBubbleMenu } from '../../../components/rich-editor/editor-bubble-menu';
import { Tooltip } from '../../../components/tooltip/Tooltip';

import styles from './weather-widget.module.scss';
type WeatherWidgetProps = {
  step: Step;
};

export const WeatherWidget: React.FC<WeatherWidgetProps> = observer(({ step }) => {
  const [isWeatherModalOpen, setIsWeatherModalOpen] = useState(false);
  const { isDesktop } = useResponsive();

  const { editor, isTextSelected } = useRichTextEditor({
    key: step._id,
    value: step.info?.generalNote || '',
    onBlur: (text) => {
      step.update({ info: { generalNote: text } });
    },
  });

  const handleUpdateWeather = async () => {
    await step.updateWeatherDetails();
  };

  return (
    <>
      <div className={styles['widget-container']}>
        <div className={styles['weather-container']}>
          <div className={styles['sun-data-wrapper']} style={{ fontWeight: '400' }}>
            <CloudAndWindy />

            {step.info?.weather?.description || '-'}
          </div>

          <div className={styles['temperature']}>
            {step.info?.weather?.temp?.min || '-'}/{step.info?.weather?.temp?.max || '-'}
          </div>

          <div className={styles['weather-wrapper']}>
            <div className={styles['sun-data-wrapper']}>
              <Sunrise />

              <span>
                Sunrise:&nbsp;
                <span className={styles['sun-time']}>{step.info?.weather?.sunrise || '-'}</span>
              </span>
            </div>

            <div className={styles['sun-data-wrapper']}>
              <Sunset />

              <span>
                Sunset:&nbsp;
                <span className={styles['sun-time']}>{step.info?.weather?.sunset || '-'}</span>
              </span>
            </div>
          </div>
        </div>

        <div className={styles['modified-button-wrapper']}>
          <Tooltip text="Update weather, police, hospital" delay={500}>
            <IconButton
              icon={<UpdateIcon />}
              onClick={handleUpdateWeather}
              size={isDesktop ? 'medium' : 'small'}
            />
          </Tooltip>

          <IconButton
            variant="dark"
            icon={<Pencil1Icon />}
            onClick={() => setIsWeatherModalOpen(true)}
            size={isDesktop ? 'medium' : 'small'}
          />
        </div>
      </div>

      <InfoCard
        step={step}
        projectId={step.projectId}
        startDate={step.startDate}
        dueDate={step.dueDate}
        generalCall={step.info?.generalCallTime || { hour: 0, minute: 0 }}
      />

      <div className={styles['note-wrapper']}>
        <div className={styles['note-header']}>General note</div>

        {editor && isTextSelected && <EditorBubbleMenu editor={editor} />}
        <EditorContent
          className={styles['notes-input']}
          editor={editor}
          placeholder="Add a general note"
          rows={4}
          cols={30}
        />
      </div>

      {isWeatherModalOpen && (
        <WeatherModal
          onClose={() => setIsWeatherModalOpen(false)}
          step={step}
          defaultValues={step.info?.weather}
        />
      )}
    </>
  );
});
